import { createStore } from "vuex";
import { DateTime } from "luxon";

import AccountService from "@/services/accountService";
import SettingsService from "@/services/settingsService";
import DayService from "@/services/dayService";
import GroupsService from "@/services/groupsService";
import NotesService from "@/services/notesService";
import IdeasService from "@/services/ideasService";

import AdminModule from "./modules/admin";

function addToast(state, message, variant) {
    let title;
    let timeout;
    let color;

    if (variant === "error") {
        title = "Фатальная ошибка";
        timeout = -1;
        color = "error";
    } else if (variant === "warning") {
        title = "Ошибка";
        timeout = -1;
        color = "error";
    } else if (variant === "success") {
        title = "Оповещение";
        timeout = 2000;
        color = "success";
    } else {
        return;
    }

    state.toasts.push({
        title,
        message,
        variant,
        timeout,
        color
    });

    state.toasts = state.toasts.slice();
}

export default createStore({
    modules: {
        admin: AdminModule
    },

    state: {
        // Services:

        accountService: new AccountService(),
        settingsService: new SettingsService(),
        dayService: new DayService(),
        groupsService: new GroupsService(),
        notesService: new NotesService(),
        ideasService: new IdeasService(),

        // All:

        loadingsCount: 0,
        toasts: [],
        currentLocale: "ru",

        // Users:

        currentDateTime: DateTime.now(),
        selectedDateTime: null,
        startDateTime: null,
        endDateTime: null,

        showDateDialog: false,
        showDateRangeDialog: false,
        showThemeDialog: false,

        userGroups: [],
        selectedGroup: null,

        userInvitations: [],
        sentInvitations: [],

        // Account:

        userId: window.AcademIt.UserId,
        userName: window.AcademIt.UserName,
        userRoles: window.AcademIt.Roles,

        isAuthorized: window.AcademIt.UserId !== "",
        isAdministrator: window.AcademIt.Roles.includes("Administrator"),
        isClient: window.AcademIt.Roles.includes("Client"),

        passwordOptions: null,

        // Settings:

        userSettings: null,
        clientSettings: null,

        // Day:

        morningCitation: null,
        eveningCitation: null,

        daysResults: [],
        daysTasks: [],

        habits: [],

        notes: [],

        ideas: []
    },

    getters: {
        isLoading: state => {
            return state.loadingsCount > 0;
        },

        datesRange: state => {
            const isDateRange = state.startDateTime && state.endDateTime;

            const startDate = isDateRange
                ? state.startDateTime
                : state.selectedDateTime
                    ? state.selectedDateTime
                    : state.currentDateTime.startOf("day");

            const endDate = isDateRange
                ? state.endDateTime
                : state.selectedDateTime
                    ? state.selectedDateTime
                    : state.currentDateTime.startOf("day");

            return {
                startDate: startDate,
                endDate: endDate
            };
        }
    },

    mutations: {
        // All:

        setToasts(state, newValue) {
            state.toasts = newValue;
        },

        success(state, message) {
            addToast(state, message, "success");
        },

        error(state, message) {
            addToast(state, message, "error");
        },

        warning(state, message) {
            addToast(state, message, "warning");
        },

        setCurrentLocale(state, newValue) {
            state.currentLocale = newValue;
        },

        // Users:

        setCurrentDateTime(state, newValue) {
            if (newValue !== undefined) {
                state.currentDateTime = newValue;
            } else {
                state.currentDateTime = DateTime.now();
            }
        },

        setSelectedDateTime(state, newValue) {
            state.selectedDateTime = newValue;
        },

        setStartDateTime(state, newValue) {
            state.startDateTime = newValue;
        },

        setEndDateTime(state, newValue) {
            state.endDateTime = newValue;
        },

        setShowDateDialog(state, newValue) {
            state.showDateDialog = newValue;
        },

        setShowDateRangeDialog(state, newValue) {
            state.showDateRangeDialog = newValue;
        },

        setShowThemeDialog(state, newValue) {
            state.showThemeDialog = newValue;
        },

        setUserGroups(state, newValue) {
            state.userGroups = newValue;
        },

        setSelectedGroup(state, newValue) {
            state.selectedGroup = newValue;
        },

        setUserInvitations(state, newValue) {
            state.userInvitations = newValue;
        },

        setSentInvitations(state, newValue) {
            state.sentInvitations = newValue;
        },

        // Account:

        startLoading(state) {
            ++state.loadingsCount;
        },

        stopLoading(state) {
            state.loadingsCount = Math.max(state.loadingsCount - 1, 0);
        },

        setPasswordOptions(state, newValue) {
            state.passwordOptions = newValue;
        },

        // Settings:

        setUserSettings(state, newValue) {
            state.userSettings = newValue;
        },

        setClientSettings(state, newValue) {
            state.clientSettings = newValue;
        },

        // Day:

        setClientCitations(state, newValue) {
            state.morningCitation = newValue?.morningCitation ?? null;
            state.eveningCitation = newValue?.eveningCitation ?? null;
        },

        setMorningCitation(state, newValue) {
            state.morningCitation = newValue;
        },

        setEveningCitation(state, newValue) {
            state.eveningCitation = newValue;
        },

        setDaysResults(state, newValue) {
            if (!newValue) {
                state.daysResults = [];
            } else {
                state.daysResults = newValue;
            }
        },

        setDaysTasks(state, newValue) {
            if (!newValue) {
                state.daysTasks = [];
            } else {
                state.daysTasks = newValue;
            }
        },

        setHabits(state, newValue) {
            if (!newValue) {
                state.habits = [];
            } else {
                state.habits = newValue;
            }
        },

        setNotes(state, newValue) {
            if (!newValue) {
                state.notes = [];
            } else {
                state.notes = newValue;
            }
        },

        setIdeas(state, newValue) {
            if (!newValue) {
                state.ideas = [];
            } else {
                state.ideas = newValue;
            }
        }
    },

    actions: {
        //#region Users

        loadUserGroups({ state, commit }) {
            commit("startLoading");
            return state.groupsService.getUserGroups()
                .then(response => commit("setUserGroups", response.data))
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        upsertGroup({ state, commit }, group) {
            return state.groupsService.upsertGroup(group)
                .then(response => {
                    if (response.data && !response.data.isSuccess) {
                        commit("warning", response.data.message);
                        return;
                    }
                })
                .catch(error => commit("error", error.message));
        },

        loadUserInvitations({ state, commit }) {
            commit("startLoading");
            return state.groupsService.getUserInvitations()
                .then(response => commit("setUserInvitations", response.data))
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        loadSentInvitations({ state, commit }) {
            commit("startLoading");
            return state.groupsService.getSentInvitations()
                .then(response => commit("setSentInvitations", response.data))
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        createInvitation({ state, commit }, request) {
            return state.groupsService.createInvitation(request)
                .then(response => {
                    if (response.data && !response.data.isSuccess) {
                        commit("warning", response.data.message);
                        return;
                    }
                })
                .catch(error => commit("error", error.message));
        },

        respondToInvitation({ state, commit }, request) {
            return state.groupsService.respondToInvitation(request)
                .then(response => {
                    if (response.data && !response.data.isSuccess) {
                        commit("warning", response.data.message);
                        return;
                    }
                })
                .catch(error => commit("error", error.message));
        },

        //#endregion

        //#region Account

        login({ state, commit }, request) {
            commit("startLoading");
            return state.accountService.login(request)
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        logout({ state, commit }) {
            commit("startLoading");
            return state.accountService.logout()
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        registerClient({ state, commit }, request) {
            commit("startLoading");
            return state.accountService.registerClient(request)
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        confirmEmail({ state, commit }, request) {
            commit("startLoading");
            return state.accountService.confirmEmail(request)
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        changePassword({ state, commit }, request) {
            commit("startLoading");
            return state.accountService.changePassword(request)
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        sendRestorePasswordEmail({ state, commit }, email) {
            commit("startLoading");
            return state.accountService.sendRestorePasswordEmail(email)
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        resetPassword({ state, commit }, request) {
            commit("startLoading");
            return state.accountService.resetPassword(request)
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        loadPasswordOptions({ state, commit }) {
            return state.accountService.getPasswordOptions().then(response => {
                commit("setPasswordOptions", response.data);
            });
        },

        //#endregion

        //#region Settings

        loadUserSettings({ state, commit }) {
            commit("startLoading");
            return state.settingsService.getUserSettings()
                .then(response => commit("setUserSettings", response.data))
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        saveUserSettings({ state, commit }, request) {
            return state.settingsService.setUserSettings(request)
                .catch(error => commit("error", error.message));
        },

        loadClientSettings({ state, commit }) {
            commit("startLoading");
            return state.settingsService.getClientSettings()
                .then(response => commit("setClientSettings", response.data))
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        saveClientSettings({ state, commit }, request) {
            return state.settingsService.setClientSettings(request)
                .catch(error => commit("error", error.message));
        },

        //#endregion

        //#region Day

        loadClientCitations({ state, commit }) {
            commit("startLoading");
            return state.dayService.getClientCitations()
                .then(response => commit("setClientCitations", response.data))
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        loadDaysResults({ state, commit, getters }) {
            const request = {
                startDate: getters.datesRange.startDate.toFormat("yyyy-MM-dd"),
                endDate: getters.datesRange.endDate.toFormat("yyyy-MM-dd")
            };

            commit("startLoading");
            return state.dayService.getDaysResults(request.startDate, request.endDate)
                .then(response => commit("setDaysResults", response.data))
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        upsertDayResult({ state, commit }, dayResult) {
            return state.dayService.upsertDayResult(dayResult)
                .catch(error => commit("error", error.message));
        },

        loadDaysTasks({ state, commit, getters }) {
            const request = {
                startDate: getters.datesRange.startDate.toFormat("yyyy-MM-dd"),
                endDate: getters.datesRange.endDate.toFormat("yyyy-MM-dd")
            };

            commit("startLoading");
            return state.dayService.getDaysTasks(request.startDate, request.endDate)
                .then(response => commit("setDaysTasks", response.data))
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        upsertDayTask({ state, commit }, dayTask) {
            return state.dayService.upsertDayTask(dayTask)
                .catch(error => commit("error", error.message));
        },

        delegateDayTask({ state, commit }, request) {
            return state.dayService.delegateDayTask(request)
                .catch(error => commit("error", error.message));
        },

        //#endregion

        //#region Habits

        loadHabits({ state, commit, getters }) {
            commit("startLoading");
            return state.dayService.getActiveHabits(getters.datesRange.startDate.toFormat("yyyy-MM-dd"), getters.datesRange.endDate.toFormat("yyyy-MM-dd"))
                .then(response => commit("setHabits", response.data))
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        createHabit({ state, commit }, habit) {
            return state.dayService.createHabit(habit)
                .catch(error => commit("error", error.message));
        },

        editHabit({ state, commit }, habit) {
            return state.dayService.editHabit(habit)
                .catch(error => commit("error", error.message));
        },

        deleteHabit({ state, commit }, id) {
            return state.dayService.deleteHabit(id)
                .catch(error => commit("error", error.message));
        },

        //#endregion

        //#region Notes  

        loadNotes({ state, commit }) {
            commit("startLoading");

            return state.notesService.getClientNotes()
                .then(response => commit("setNotes", response.data))
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        createNote({ state, commit }, note) {
            return state.notesService.createNote(note)
                .catch(error => commit("error", error.message));
        },

        editNote({ state, commit }, note) {
            return state.notesService.editNote(note)
                .catch(error => commit("error", error.message));
        },

        deleteNote({ state, commit }, note) {
            return state.notesService.deleteNote(note)
                .catch(error => commit("error", error.message));
        },

        saveNotesOrder({ state, commit }, notes) {
            return state.notesService.saveOrder(notes)
                .catch(error => commit("error", error.message));
        },

        //#endregion

        //#region Ideas

        loadIdeas({ state, commit }) {
            commit("startLoading");

            return state.ideasService.getClientIdeas()
                .then(response => commit("setIdeas", response.data))
                .catch(error => commit("error", error.message))
                .finally(() => commit("stopLoading"));
        },

        createIdea({ state, commit }, idea) {
            return state.ideasService.createIdea(idea)
                .catch(error => commit("error", error.message));
        },

        editIdea({ state, commit }, idea) {
            return state.ideasService.editIdea(idea)
                .catch(error => commit("error", error.message));
        },

        deleteIdea({ state, commit }, idea) {
            return state.ideasService.deleteIdea(idea)
                .catch(error => commit("error", error.message));
        },

        saveIdeasOrder({ state, commit }, ideas) {
            return state.ideasService.saveOrder(ideas)
                .catch(error => commit("error", error.message));
        }

        //#endregion
    }
});
